import React, { useEffect, useMemo, useRef, useState } from 'react'
import type { Theme } from '@material-ui/core'
import { makeStyles } from '@material-ui/core'
import CloudUploadIcon from '@material-ui/icons/CloudUploadOutlined'
import classnames from 'classnames'
import SplitterLayout from 'react-splitter-layout'
import { useDropzone } from 'react-dropzone'
import FileViewerGallery from './FileViewerGallery'
import type { FileViewerGalleryFile } from './FileViewerGallery'
import FileViewerDetails from './FileViewerDetails'
import FileViewerEditor, { type FileEditorSavePayload } from './FileViewerEditor'
import FileViewerButton from './FileViewerButton'
import { useIsMobile } from '../../hooks/useIsMobile'
import { useLocalStorage } from '../../hooks/useLocalStorage'
import { useTranslation } from '../../providers/TranslationProvider'
import { hiddenFileInputStyle, DEFAULT_FILE_ACCEPT } from '../Dropzone'
import { clampGallerySize, DEFAULT_GALLERY_SIZE, GALLERY_SIZE_STORAGE_KEY, MIN_GALLERY_SIZE } from './gallerySize'

export type FileViewerFile = FileViewerGalleryFile

export interface FileViewerProps {
  files: FileViewerFile[]
  initialKey?: string
  /**
   * Controlled selection. When `onSelectedKeyChange` is supplied the viewer
   * becomes controlled: `selectedKey` drives which file's details show, and every
   * selection (gallery click, programmatic) is reported back so the caller can
   * command navigation — e.g. jump to the photo whose caption is over the limit
   * when a save is blocked. Omit both to keep the viewer uncontrolled (internal
   * state seeded from `initialKey`).
   */
  selectedKey?: string
  onSelectedKeyChange?: (key: string) => void
  /**
   * Called whenever a file's metadata changes (title, description, show-on).
   * Caller is responsible for persisting changes.
   */
  onFileChange: (file: FileViewerFile) => void

  /**
   * Called when the user finishes editing an image in the in-app editor.
   * The caller is responsible for uploading the resulting blob and returning
   * the updated QuoteFile (with new src and originalSrc stashed appropriately).
   */
  onEditSave: (payload: FileEditorSavePayload, file: FileViewerFile) => Promise<FileViewerFile> | FileViewerFile

  /**
   * Triggers the upload picker. Caller wires up the actual upload.
   */
  onUpload?: (files: File[]) => void

  /**
   * When supplied, clicking the gallery's Upload tile calls this instead of
   * opening the OS file picker — used to surface a richer "add files" dialog
   * (device upload + provider tabs). Drag-and-drop onto the gallery still routes
   * straight through `onUpload`, so the fast-path is preserved.
   */
  onPickFiles?: () => void

  /**
   * When supplied, renders an "add from provider" affordance in the gallery that
   * reopens a provider's own picker (e.g. CompanyCam) so more provider photos can
   * be added without leaving the viewer. `addProviderLabel` supplies the wording.
   */
  onAddProvider?: () => void
  /** Label/tooltip for the `onAddProvider` affordance, e.g. "Add from CompanyCam". */
  addProviderLabel?: string

  /**
   * When supplied, gallery thumbnails become drag-to-reorder. Called with the
   * full file list in its new order; the caller persists it. Selection is by
   * key, so the previewed file is preserved across a reorder.
   */
  onReorder?: (files: FileViewerFile[]) => void

  /**
   * Replace the currently selected file's content. Caller handles the upload.
   */
  onChangeFile?: (replacement: File, target: FileViewerFile) => void

  /**
   * Called instead of the file picker when Change is clicked on a provider-sourced
   * file (e.g. CompanyCam). Lets the caller reopen the provider's own picker rather
   * than the OS file dialog. Only used when the selected file has a `provider`.
   */
  onChangeProviderFile?: (target: FileViewerFile) => void

  onDelete?: (file: FileViewerFile) => void

  /**
   * Delete several files at once. Called from the multi-select bulk panel
   * after the user confirms. When omitted, multi-select is disabled — the
   * gallery's `Select all` button/menu won't render.
   */
  onDeleteMany?: (keys: string[]) => void

  /** Hide description + Show on. */
  titleOnly?: boolean

  /** Read-only — disables editing, deleting, replacing. */
  viewOnly?: boolean

  /**
   * Fires whenever the editor opens or closes. Parents (e.g. FileViewerDialog)
   * use this to swap their footer between "Cancel | Done" (gallery mode) and
   * a hidden footer (editor mode — the editor renders its own).
   */
  onEditingChange?: (editing: boolean) => void

  /**
   * Fires whenever the open editor's unsaved-changes state changes (and resets to
   * false when the editor closes). Parents (e.g. FileViewerDialog) use this so the
   * corner ✕ can prompt before discarding markup.
   */
  onEditorDirtyChange?: (dirty: boolean) => void
}

const useStyles = makeStyles<Theme>((theme) => ({
  root: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    // Anchor for the full-viewer drop overlay (#21418).
    position: 'relative'
  },
  // Covers the whole viewer while files are dragged over it, so the drop target
  // is the entire dialog body rather than the small gallery Upload tile (#21418).
  // pointer-events:none lets the underlying drop zone keep receiving the drag.
  dropOverlay: {
    position: 'absolute',
    inset: theme.spacing(1),
    zIndex: 20,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(1),
    pointerEvents: 'none',
    borderRadius: theme.shape.borderRadius,
    border: `2px dashed ${theme.palette.primary.main}`,
    background: 'rgba(255, 255, 255, 0.9)',
    color: theme.palette.primary.main,
    textAlign: 'center'
  },
  dropOverlayReject: {
    borderColor: theme.palette.error.main,
    color: theme.palette.error.main
  },
  dropOverlayIcon: {
    fontSize: 56
  },
  dropOverlayLabel: {
    ...theme.typography.h6,
    fontWeight: theme.typography.fontWeightMedium
  },
  splitter: {
    flex: 1,
    minHeight: 0,
    position: 'relative',
    '& .splitter-layout': {
      position: 'absolute',
      inset: 0,
      display: 'flex',
      flexDirection: 'row',
      width: '100%',
      height: '100%',
      overflow: 'hidden'
    },
    '& .splitter-layout > .layout-pane': {
      position: 'relative',
      flex: '0 0 auto',
      overflow: 'hidden'
    },
    '& .splitter-layout > .layout-pane.layout-pane-primary': {
      flex: '1 1 auto'
    },
    '& .splitter-layout > .layout-splitter': {
      flex: '0 0 auto',
      width: 8,
      height: '100%',
      cursor: 'col-resize',
      background: theme.paintscout.palette.brand.Neutral.P96,
      borderLeft: `1px solid ${theme.paintscout.palette.brand.Neutral.P90}`,
      borderRight: `1px solid ${theme.paintscout.palette.brand.Neutral.P90}`,
      transition: 'background-color 120ms ease',
      '&:hover': {
        background: theme.paintscout.palette.brand.Neutral.P90
      }
    }
  },
  mobileStack: {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden'
  },
  mobileDetails: {
    flex: '1 1 auto',
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden'
  },
  mobileGallery: {
    flex: '0 0 auto',
    borderTop: `1px solid ${theme.paintscout.palette.brand.Neutral.P90}`,
    background: theme.palette.common.white
  },
  bulkPanel: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    background: theme.palette.common.white
  },
  bulkHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(1.5, 2, 1, 2),
    minHeight: 44,
    borderBottom: `1px solid ${theme.paintscout.palette.brand.Neutral.P90}`
  },
  bulkCount: {
    ...theme.typography.subtitle1,
    fontWeight: theme.typography.fontWeightMedium,
    color: theme.paintscout.palette.brand.Neutral.P30
  },
  bulkBody: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(3),
    textAlign: 'center',
    color: theme.paintscout.palette.brand.Neutral.P50,
    gap: theme.spacing(2)
  },
  bulkActions: {
    display: 'flex',
    gap: theme.spacing(1)
  }
}))

function FileViewer(props: FileViewerProps) {
  const {
    files,
    initialKey,
    selectedKey: controlledKey,
    onSelectedKeyChange,
    onFileChange,
    onEditSave,
    onUpload,
    onPickFiles,
    onAddProvider,
    addProviderLabel,
    onReorder,
    onChangeFile,
    onChangeProviderFile,
    onDelete,
    onDeleteMany,
    titleOnly,
    viewOnly,
    onEditingChange,
    onEditorDirtyChange
  } = props
  const classes = useStyles({})
  const { __ } = useTranslation()
  const isMobile = useIsMobile()
  // Selection is controlled iff the caller passes `onSelectedKeyChange` (a stable
  // signal — avoids the controlled/uncontrolled flip that keying off a possibly-
  // undefined `selectedKey` value would cause). Uncontrolled callers keep the
  // internal state seeded from `initialKey`.
  const controlled = !!onSelectedKeyChange
  const [internalKey, setInternalKey] = useState<string | undefined>(initialKey || files[0]?.key)
  const selectedKey = controlled ? controlledKey : internalKey
  const selectKey = (key: string) => {
    if (controlled) onSelectedKeyChange?.(key)
    else setInternalKey(key)
  }
  // Warn (dev only) if a caller passes `selectedKey` without `onSelectedKeyChange`:
  // the viewer then stays uncontrolled and silently ignores `selectedKey`, which is
  // an easy footgun given both props sit on the interface.
  useEffect(() => {
    if (process.env.NODE_ENV !== 'production' && controlledKey !== undefined && !onSelectedKeyChange) {
      console.warn(
        'FileViewer: `selectedKey` was provided without `onSelectedKeyChange`. Selection stays ' +
          'uncontrolled and `selectedKey` is ignored — pass `onSelectedKeyChange` to control selection.'
      )
    }
  }, [controlledKey, onSelectedKeyChange])
  const [editing, setEditing] = useState(false)
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(() => new Set())
  const uploadInputRef = useRef<HTMLInputElement>(null)
  const replaceInputRef = useRef<HTMLInputElement>(null)
  // Persist the grey-bar (splitter) placement so a preferred — e.g. condensed —
  // thumbnail strip width sticks across opens. The desktop SplitterLayout mounts
  // fresh each open, so secondaryInitialSize reads the stored value on mount.
  const [gallerySize, setGallerySize] = useLocalStorage<number>(GALLERY_SIZE_STORAGE_KEY, DEFAULT_GALLERY_SIZE)
  // Holds the live size only while a drag is in progress. Starts undefined (no
  // mount-time snapshot) so a drag-end can never persist a stale value, and is
  // reset after each commit.
  const galleryDragSizeRef = useRef<number | undefined>()

  const canUpload = !!onUpload

  // Viewer-wide drop zone (#21418). Dropping anywhere over the viewer body — not
  // just the small gallery Upload tile — routes straight to the fast upload path
  // (onUpload). Disabled while the image editor is open so a drop can't clobber
  // the editing canvas. noClick/noKeyboard: the tile + hidden input still own the
  // click-to-pick affordance.
  const { getRootProps, isDragActive, isDragReject, draggedFiles } = useDropzone({
    onDrop: (accepted) => {
      if (accepted.length) onUpload?.(accepted)
    },
    accept: DEFAULT_FILE_ACCEPT,
    multiple: true,
    noClick: true,
    noKeyboard: true,
    disabled: !canUpload || editing
  })
  // Chrome doesn't populate dragged file types during dragover, so isDragReject
  // alone would flag every drag as invalid. `draggedFiles` is part of
  // react-dropzone v10's public state (dropped in v12+ — revisit on any bump).
  const rejectDrag = isDragReject && draggedFiles.some((f) => f.type !== '')
  const showDropOverlay = canUpload && !editing && isDragActive

  // Swallow drags that miss the drop zone (title bar, footer, backdrop) so the
  // browser never navigates to / opens a dropped file in a new tab — the exact
  // failure this issue calls out. Mirrors the window guard in Dropzone.tsx.
  // Keyed on the stable `canUpload` boolean rather than `onUpload`'s identity, so
  // an inline-arrow callback at the call site can't re-register the listeners on
  // every render (React still pairs each cleanup with the closure it added, but
  // this avoids the per-render add/remove churn entirely).
  useEffect(() => {
    if (!canUpload) return
    const preventDefault = (e: DragEvent) => e.preventDefault()
    window.addEventListener('dragenter', preventDefault, false)
    window.addEventListener('dragover', preventDefault)
    window.addEventListener('drop', preventDefault)
    return () => {
      window.removeEventListener('dragenter', preventDefault)
      window.removeEventListener('dragover', preventDefault)
      window.removeEventListener('drop', preventDefault)
    }
  }, [canUpload])

  const selected = files.find((f) => f.key === selectedKey) || files[0]
  const inMultiSelectMode = selectedKeys.size > 0

  // Change button: provider-sourced files (e.g. CompanyCam) reopen the provider's
  // own picker; everything else opens the OS file dialog via the hidden input.
  const handleChangeClick = onChangeFile
    ? selected?.provider && onChangeProviderFile
      ? () => onChangeProviderFile(selected)
      : () => replaceInputRef.current?.click()
    : undefined
  const allFileKeys = useMemo(() => files.map((f) => f.key), [files])

  const setEditingAndNotify = (next: boolean) => {
    setEditing(next)
    onEditingChange?.(next)
  }

  const handleEditSave = async (payload: FileEditorSavePayload) => {
    if (!selected) return
    const updated = await onEditSave(payload, selected)
    onFileChange(updated)
    setEditingAndNotify(false)
  }

  const handleSelectAll = onDeleteMany
    ? () => {
        setSelectedKeys((prev) => {
          if (allFileKeys.length > 0 && allFileKeys.every((k) => prev.has(k))) {
            return new Set()
          }
          return new Set(allFileKeys)
        })
      }
    : undefined

  const handleToggleSelect = (file: FileViewerFile) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev)
      if (next.has(file.key)) {
        next.delete(file.key)
      } else {
        next.add(file.key)
      }
      return next
    })
  }

  const handleBulkDelete = () => {
    if (!onDeleteMany) return
    const keys = Array.from(selectedKeys)
    if (keys.length === 0) return
    onDeleteMany(keys)
    setSelectedKeys(new Set())
  }

  const handleClearSelection = () => setSelectedKeys(new Set())

  const bulkPanel = (
    <div className={classes.bulkPanel}>
      <div className={classes.bulkHeader}>
        <span className={classes.bulkCount}>{__('%d selected', selectedKeys.size)}</span>
      </div>
      <div className={classes.bulkBody}>
        <div>
          {selectedKeys.size === 1
            ? __('1 file will be removed when you click Delete.')
            : __('%d files will be removed when you click Delete.', selectedKeys.size)}
        </div>
        <div className={classes.bulkActions}>
          <FileViewerButton onClick={handleClearSelection} tone="neutral">
            {__('Cancel')}
          </FileViewerButton>
          <FileViewerButton onClick={handleBulkDelete} tone="danger">
            {__('Delete')}
          </FileViewerButton>
        </div>
      </div>
    </div>
  )

  return (
    <div className={classes.root} {...getRootProps()}>
      {showDropOverlay && (
        <div className={classnames(classes.dropOverlay, rejectDrag && classes.dropOverlayReject)}>
          <CloudUploadIcon className={classes.dropOverlayIcon} />
          <span className={classes.dropOverlayLabel}>
            {rejectDrag ? __('Invalid file type') : __('Drop files to upload')}
          </span>
        </div>
      )}
      <div className={classes.splitter}>
        {editing && selected ? (
          // Full-bleed editor — gallery is hidden until the user clicks Back to files.
          <FileViewerEditor
            file={selected}
            onBack={() => setEditingAndNotify(false)}
            onSave={handleEditSave}
            onDirtyChange={onEditorDirtyChange}
          />
        ) : isMobile ? (
          <div className={classes.mobileStack}>
            <div className={classes.mobileDetails}>
              {inMultiSelectMode ? (
                bulkPanel
              ) : selected ? (
                <FileViewerDetails
                  file={selected}
                  onChange={onFileChange}
                  onEdit={() => setEditingAndNotify(true)}
                  onChangeFile={handleChangeClick}
                  onDelete={onDelete ? () => onDelete(selected) : undefined}
                  titleOnly={titleOnly}
                  viewOnly={viewOnly}
                  compact
                />
              ) : null}
            </div>
            <div className={classes.mobileGallery}>
              <FileViewerGallery
                files={files}
                selectedKey={selected?.key}
                onSelect={(f) => {
                  selectKey(f.key)
                }}
                onSelectAll={handleSelectAll}
                onUploadClick={onPickFiles ?? (onUpload ? () => uploadInputRef.current?.click() : undefined)}
                onAddProvider={onAddProvider}
                addProviderLabel={addProviderLabel}
                onReorder={onReorder}
                selectedKeys={selectedKeys}
                onToggleSelect={onDeleteMany ? handleToggleSelect : undefined}
                orientation="horizontal"
              />
            </div>
          </div>
        ) : (
          <SplitterLayout
            customClassName="file-viewer-splitter"
            primaryIndex={1}
            primaryMinSize={320}
            secondaryMinSize={MIN_GALLERY_SIZE}
            secondaryInitialSize={clampGallerySize(gallerySize)}
            onSecondaryPaneSizeChange={(size) => {
              galleryDragSizeRef.current = size
            }}
            onDragEnd={() => {
              if (galleryDragSizeRef.current === undefined) return
              setGallerySize(clampGallerySize(galleryDragSizeRef.current))
              galleryDragSizeRef.current = undefined
            }}
          >
            <FileViewerGallery
              files={files}
              selectedKey={selected?.key}
              onSelect={(f) => {
                selectKey(f.key)
              }}
              onSelectAll={handleSelectAll}
              onUploadClick={onPickFiles ?? (onUpload ? () => uploadInputRef.current?.click() : undefined)}
              onAddProvider={onAddProvider}
              addProviderLabel={addProviderLabel}
              onReorder={onReorder}
              selectedKeys={selectedKeys}
              onToggleSelect={onDeleteMany ? handleToggleSelect : undefined}
            />
            {inMultiSelectMode ? (
              bulkPanel
            ) : selected ? (
              <FileViewerDetails
                file={selected}
                onChange={onFileChange}
                onEdit={() => setEditingAndNotify(true)}
                onChangeFile={handleChangeClick}
                onDelete={onDelete ? () => onDelete(selected) : undefined}
                titleOnly={titleOnly}
                viewOnly={viewOnly}
              />
            ) : (
              <div />
            )}
          </SplitterLayout>
        )}
      </div>

      {onUpload && (
        <input
          ref={uploadInputRef}
          type="file"
          multiple
          accept={DEFAULT_FILE_ACCEPT}
          style={hiddenFileInputStyle}
          onChange={(e) => {
            const list = e.target.files
            if (list && list.length) onUpload(Array.from(list))
            e.target.value = ''
          }}
        />
      )}
      {onChangeFile && (
        <input
          ref={replaceInputRef}
          type="file"
          accept={DEFAULT_FILE_ACCEPT}
          style={hiddenFileInputStyle}
          onChange={(e) => {
            const list = e.target.files
            if (list && list[0] && selected) onChangeFile(list[0], selected)
            e.target.value = ''
          }}
        />
      )}
    </div>
  )
}

export default FileViewer
